import { getTokenExpiration, isTokenExpiringSoon } from './jwtUtils';
import { getTokens, isUsableRefreshToken } from './tokens';

/**
 * Resolves a usable access token, refreshing first when the stored one is
 * expired or close to it.
 */
export type AccessTokenProvider = () => Promise<string>;

interface StoredTokens {
  token?: string | null;
  refresh_token?: string | null;
}

/**
 * Asks whether the token is expired right now rather than whether it soon will
 * be. The provider's 5 minute default would end a still-valid IRP session five
 * minutes early, and IRP has no refresh token to recover with.
 */
const NO_EXPIRY_BUFFER_MINUTES = 0;

/**
 * Implemented by an error whose failure will not change on a later attempt —
 * e.g. no refresh token to retry with. Retry logic can check this instead of
 * matching specific error classes, so a future non-retryable error needs no
 * changes at the call sites that decide whether to retry.
 */
export interface NonRetryableError extends Error {
  readonly retryable: false;
}

/**
 * Structural check for {@link NonRetryableError}, not a class check, so it
 * also recognizes a future non-retryable error defined outside this module.
 */
export function isNonRetryableError(
  error: unknown,
): error is NonRetryableError {
  return (
    error instanceof Error && 'retryable' in error && error.retryable === false
  );
}

/**
 * Raised when the stored access token has already expired and there is no
 * refresh token to replace it. `expiredForMs` lets a caller report how stale
 * the token was; nothing else from the token is exposed, because this value
 * reaches logs and telemetry.
 *
 * The message stays constant and the age travels as a property, because Sentry
 * falls back to grouping by message whenever a stacktrace is unusable, and
 * minified bundles with imperfect source maps are exactly when that happens.
 * An interpolated age would scatter one fault across an issue per event.
 */
export class ExpiredAccessTokenError
  extends Error
  implements NonRetryableError
{
  readonly expiredForMs: number;
  readonly retryable = false;

  constructor(expiredForMs: number) {
    super(
      'Access token has expired and there is no refresh token to get a new one',
    );
    this.name = 'ExpiredAccessTokenError';
    this.expiredForMs = expiredForMs;
  }
}

/**
 * Raised when there is no stored access token at all and no refresh token to
 * obtain one. Nothing short of the user re-authenticating can resolve this.
 */
export class NoRefreshTokenError extends Error implements NonRetryableError {
  readonly retryable = false;

  constructor() {
    super('No access token available and no refresh token to get one');
    this.name = 'NoRefreshTokenError';
  }
}

/**
 * Performs the refresh round trip and persists the result.
 *
 * Injected rather than called directly, because the refresh has to reach Redux
 * as well as storage: the provider's callers pass
 * `refreshToken => dispatch(refreshUserToken(refreshToken))`, and that
 * dispatch is what updates `sinatra.auth.tokens.token` — the value rsosApi's
 * `prepareHeaders` puts on outgoing requests. Calling the shared writer here
 * would persist to storage and leave Redux holding the old token.
 *
 * De-duplication of concurrent refreshes lives in the writer underneath, so it
 * now covers the sockets, the RTK Query reauth path and the midas interceptor
 * together rather than only the callers of this module.
 */
export type RefreshFn = (refreshToken: string) => Promise<unknown>;

/**
 * Builds the single async entry point callers use to obtain an access token.
 * Callers await it and get a token back; where the token is stored, when it is
 * refreshed, and who else needs to hear about the refresh are all this
 * function's problem rather than theirs.
 *
 * @param refresh - Performs the refresh round trip, e.g.
 *   `refreshToken => dispatch(refreshUserToken(refreshToken))`
 * @param options.bufferMinutes - Refresh this far ahead of expiry (default 5)
 * @returns An async function resolving to a usable access token
 */
export function createAccessTokenProvider(
  refresh: RefreshFn,
  options: { bufferMinutes?: number } = {},
): AccessTokenProvider {
  const { bufferMinutes = 5 } = options;

  return async function getAccessToken(): Promise<string> {
    const stored: StoredTokens = getTokens() || {};

    if (stored.token && !isTokenExpiringSoon(stored.token, bufferMinutes)) {
      return stored.token;
    }

    // IRP sessions are issued an access token with no refresh token. There is
    // nothing to refresh with, so a still-valid token on hand is the only
    // answer, and an expired one is no answer at all. The shared check also
    // rejects the string 'null', which older writes left in storage and which
    // would otherwise 400 the refresh endpoint.
    if (!isUsableRefreshToken(stored.refresh_token)) {
      if (stored.token) {
        const expiration = getTokenExpiration(stored.token);
        // Returning a dead token only gets the socket a 401 on the upgrade,
        // which the browser hides behind a generic close, so the client
        // retries with the same dead token until its budget runs out. Tokens
        // with no readable expiration are left alone: opaque auto-login
        // tokens are not JWTs, and isTokenExpiringSoon reports those as
        // expired.
        if (
          expiration !== null &&
          isTokenExpiringSoon(stored.token, NO_EXPIRY_BUFFER_MINUTES)
        ) {
          throw new ExpiredAccessTokenError(Date.now() - expiration);
        }
        return stored.token;
      }
      throw new NoRefreshTokenError();
    }

    try {
      await refresh(stored.refresh_token);
    } catch (error) {
      // refreshUserToken rejects with a dispatched error action rather than an
      // Error, so re-wrap it to give callers something with a stack.
      throw new Error('Access token refresh failed', { cause: error });
    }

    const refreshed: StoredTokens = getTokens() || {};
    if (!refreshed.token) {
      throw new Error('Access token missing after a successful refresh');
    }
    return refreshed.token;
  };
}

export default createAccessTokenProvider;
